import axios from "axios";
import { getApiBase } from "../apiBase";
import { buildPluginsHeader } from "./registry";
import {
  EXPANSION_CSV_BOOL_FIELDS,
  EXPANSION_CSV_NUM_FIELDS,
  EXPANSION_CSV_MATERIALS_JSON_FIELDS,
  EXPANSION_CSV_PERMITS_JSON_FIELDS,
  invertExpansionMapping,
} from "./expansionCsvImportFields";
import {
  EXPANSION_ACF_GROUPS,
  EXPANSION_GROUP_LIST,
  flatPayloadToExpansionGroups,
  mergeExpansionGroupsPartial,
  mergeExpansionIntoAcfGroups,
  projectScalarsFromExpansionGroups,
  readExpansionGroupsFromAcf,
} from "./expansionProjectAcf";
import {
  mergeCommercialDomainsPartial,
  mergeCommercialIntoAcfGroups,
  readCommercialDomains,
} from "./commercialProjectAcf";
import { COMMERCIAL_DOMAINS } from "./commercialProjectSchema";
import { applyMaterialTotalsToFirstPermit, coerceMaterialsJsonImportValue, MATERIALS_JSON_FIELD_ID } from "./materialsJsonImport";
import { coercePermitsJsonImportValue, PERMITS_JSON_FIELD_ID } from "./permitsJsonImport";

const AUTH_TOKEN_KEY = "projectBoardAuthToken";
const DEFAULT_MATCH_FIELD = `${EXPANSION_ACF_GROUPS.SUBSEGMENT_SUMMARY}.work_order`;

const EXPANSION_COMMERCIAL_IMPORT_DOMAINS = [
  COMMERCIAL_DOMAINS.ENGINEERING,
  COMMERCIAL_DOMAINS.CONSTRUCTION,
  COMMERCIAL_DOMAINS.FIBER,
  COMMERCIAL_DOMAINS.ACCOUNTING,
];

function commercialImportSlicesFromGroups(groups) {
  const out = {};
  if (!groups || typeof groups !== "object") return out;
  for (const domainKey of EXPANSION_COMMERCIAL_IMPORT_DOMAINS) {
    const slice = groups[domainKey];
    if (slice && typeof slice === "object" && !Array.isArray(slice)) out[domainKey] = { ...slice };
  }
  return out;
}

function authHeaders() {
  const h = { ...buildPluginsHeader() };
  try {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (token) h.Authorization = `Bearer ${token}`;
  } catch {
    /* ignore */
  }
  return h;
}

function toBool(v) {
  if (v === null || v === undefined || v === "") return null;
  if (typeof v === "boolean") return v;
  const s = String(v).trim().toLowerCase();
  if (["1", "true", "yes", "y"].includes(s)) return true;
  if (["0", "false", "no", "n"].includes(s)) return false;
  return null;
}

function toNum(v) {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(String(v).replace(/[$,]/g, ""));
  return Number.isFinite(n) ? n : null;
}

function coerceExpansionFieldValue(fieldId, raw) {
  if (raw === null || raw === undefined) return undefined;
  const s = typeof raw === "string" ? raw.trim() : raw;
  if (s === "") return undefined;
  if (EXPANSION_CSV_BOOL_FIELDS.has(fieldId)) {
    const b = toBool(s);
    return b === null ? undefined : b;
  }
  if (EXPANSION_CSV_NUM_FIELDS.has(fieldId)) {
    const n = toNum(s);
    return n === null ? undefined : n;
  }
  if (EXPANSION_CSV_PERMITS_JSON_FIELDS.has(fieldId)) {
    return coercePermitsJsonImportValue(fieldId, s);
  }
  if (EXPANSION_CSV_MATERIALS_JSON_FIELDS.has(fieldId)) {
    return coerceMaterialsJsonImportValue(fieldId, s);
  }
  return String(s);
}

/**
 * @param {Record<string, string>} csvRow
 * @param {Record<string, string>} destToColumn
 */
export function mappedExpansionRowToPayload(csvRow, destToColumn) {
  const payload = { _source: "import_csv_expansion" };
  for (const [fieldId, csvCol] of Object.entries(destToColumn)) {
    if (!fieldId || !csvCol) continue;
    if (fieldId === "export_slug") {
      const val = csvRow[csvCol];
      if (val != null && String(val).trim() !== "") payload.export_slug = String(val).trim();
      continue;
    }
    const val = coerceExpansionFieldValue(fieldId, csvRow[csvCol]);
    if (val !== undefined) payload[fieldId] = val;
  }
  return payload;
}

function workOrderFromRow(row) {
  const j = row?.acf_groups_json;
  const subSummary = j?.[EXPANSION_ACF_GROUPS.SUBSEGMENT_SUMMARY] ?? {};
  const sub =
    j?.[EXPANSION_ACF_GROUPS.SUBSEGMENTS] ??
    j?.["Expansion Subsegments"] ??
    {};
  const summary =
    j?.[EXPANSION_ACF_GROUPS.SUMMARY] ??
    j?.["Project Summary Test 2"] ??
    j?.project_summary_test_2 ??
    {};
  const wo = row?.work_order ?? subSummary.work_order ?? sub.work_order ?? summary.work_order ?? "";
  return String(wo).trim().toLowerCase();
}

function workOrderFromPayload(payload, matchField) {
  const fromMatch = payload[matchField];
  if (fromMatch != null && String(fromMatch).trim() !== "") {
    return String(fromMatch).trim().toLowerCase();
  }
  const subSummaryWo = payload[`${EXPANSION_ACF_GROUPS.SUBSEGMENT_SUMMARY}.work_order`];
  if (subSummaryWo != null && String(subSummaryWo).trim() !== "") {
    return String(subSummaryWo).trim().toLowerCase();
  }
  const subWo = payload[`${EXPANSION_ACF_GROUPS.SUBSEGMENTS}.work_order`];
  if (subWo != null && String(subWo).trim() !== "") return String(subWo).trim().toLowerCase();
  const sumWo = payload[`${EXPANSION_ACF_GROUPS.SUMMARY}.work_order`];
  if (sumWo != null && String(sumWo).trim() !== "") return String(sumWo).trim().toLowerCase();
  return "";
}

function flatPayloadToExpansionBody(flatPayload, existingAcf) {
  const imported = flatPayloadToExpansionGroups(flatPayload);
  const existingGroups = readExpansionGroupsFromAcf(existingAcf);
  const groups = mergeExpansionGroupsPartial(existingGroups, imported);
  const existingCommercial = readCommercialDomains(existingAcf);
  const importedCommercial = commercialImportSlicesFromGroups(imported);
  let commercialDomains = mergeCommercialDomainsPartial(existingCommercial, importedCommercial);
  const materialTotals = flatPayload[MATERIALS_JSON_FIELD_ID];
  if (materialTotals !== undefined) {
    commercialDomains = applyMaterialTotalsToFirstPermit(commercialDomains, materialTotals);
  }
  const acf_groups_json = mergeCommercialIntoAcfGroups(
    mergeExpansionIntoAcfGroups(
      existingAcf && typeof existingAcf === "object" ? existingAcf : {},
      groups
    ),
    commercialDomains
  );
  const rawScalars = projectScalarsFromExpansionGroups(groups, flatPayload);
  const scalars = {};
  for (const [k, v] of Object.entries(rawScalars)) {
    if (v === null || v === undefined) continue;
    if (typeof v === "number" && !Number.isFinite(v)) continue;
    scalars[k] = v;
  }
  const body = {
    _source: flatPayload._source || "import_csv_expansion",
    acf_groups_json,
    cvin_expansion: true,
    ...scalars,
  };
  if (flatPayload.export_slug) body.export_slug = flatPayload.export_slug;
  if (body.subphases_json === undefined) delete body.subphases_json;
  return body;
}

function toIndexMap(rows, keyFn) {
  const m = new Map();
  for (const r of rows) {
    const k = keyFn(r);
    if (!k) continue;
    m.set(k, r);
  }
  return m;
}

function registerProjectInIndexes(indexes, row) {
  if (!row || row.id == null) return;
  const woKey = workOrderFromRow(row);
  if (woKey) indexes.byWo.set(woKey, row);
  const slugKey = String(row.export_slug || "").trim().toLowerCase();
  if (slugKey) indexes.bySlug.set(slugKey, row);
}

function findExpansionMatch(indexes, payload, matchField) {
  if (matchField === "export_slug") {
    const slug = payload.export_slug != null ? String(payload.export_slug).trim().toLowerCase() : "";
    if (slug && indexes.bySlug.has(slug)) return indexes.bySlug.get(slug);
    return null;
  }
  const woKey = workOrderFromPayload(payload, matchField);
  if (woKey && indexes.byWo.has(woKey)) return indexes.byWo.get(woKey);
  const slug = payload.export_slug != null ? String(payload.export_slug).trim().toLowerCase() : "";
  if (slug && indexes.bySlug.has(slug)) return indexes.bySlug.get(slug);
  return null;
}

function bodyWithoutConflictingWorkOrder(body, indexes, projectId) {
  const wo = body.work_order;
  if (wo == null || wo === "") return body;
  const woKey = String(wo).trim().toLowerCase();
  const occupant = indexes.byWo.get(woKey);
  if (occupant && occupant.id !== projectId) {
    const next = { ...body };
    delete next.work_order;
    return next;
  }
  return body;
}

async function patchProject(base, projectId, body, headers, indexes) {
  const safe = bodyWithoutConflictingWorkOrder(body, indexes, projectId);
  const res = await axios.patch(`${base}/projects/${projectId}`, safe, { headers });
  return res.data;
}

async function createThenPatch(base, body, headers, indexes) {
  const createBody = {
    work_order: body.work_order,
    name: body.name,
    customer: body.customer,
    status: body.status,
    cvin_expansion: true,
    _source: body._source,
  };
  if (createBody.work_order) {
    const woKey = String(createBody.work_order).trim().toLowerCase();
    if (indexes.byWo.has(woKey)) delete createBody.work_order;
  }
  const c = await axios.post(`${base}/projects`, createBody, { headers });
  const row = c.data || {};
  if (row?.id == null) return row;
  return (await patchProject(base, row.id, body, headers, indexes)) || row;
}

function hasExpansionIdentity(payload, matchField) {
  if (workOrderFromPayload(payload, matchField)) return true;
  if (payload.export_slug != null && String(payload.export_slug).trim() !== "") return true;
  return false;
}

/**
 * Import expansion subsegment rows from CSV.
 */
export async function importExpansionProjectsFromCsv(csvRows, columnToDest, options = {}) {
  const matchField = options.matchField || DEFAULT_MATCH_FIELD;
  const createIfMissing = options.createIfMissing !== false;
  const onProgress = options.onProgress;
  const destToColumn = invertExpansionMapping(columnToDest);

  const base = getApiBase();
  const headers = authHeaders();
  const existingRes = await axios.get(`${base}/projects`, { headers });
  const existing = Array.isArray(existingRes.data) ? existingRes.data : [];
  const indexes = {
    byWo: toIndexMap(existing, workOrderFromRow),
    bySlug: toIndexMap(existing, (r) => String(r.export_slug || "").trim().toLowerCase()),
  };

  let created = 0;
  let updated = 0;
  let skipped = 0;
  const errors = [];

  for (let i = 0; i < csvRows.length; i += 1) {
    const flatPayload = mappedExpansionRowToPayload(csvRows[i], destToColumn);
    const permitsCol = destToColumn[PERMITS_JSON_FIELD_ID];
    const permitsRaw = permitsCol ? csvRows[i][permitsCol] : "";
    if (
      permitsCol &&
      permitsRaw != null &&
      String(permitsRaw).trim() !== "" &&
      flatPayload[PERMITS_JSON_FIELD_ID] === undefined
    ) {
      errors.push(
        `Row ${i + 2}: could not parse permits JSON in column "${permitsCol}" (check empty "" fields and keep JSON on one line or fully quoted in CSV)`
      );
    }
    const materialsCol = destToColumn[MATERIALS_JSON_FIELD_ID];
    const materialsRaw = materialsCol ? csvRows[i][materialsCol] : "";
    if (
      materialsCol &&
      materialsRaw != null &&
      String(materialsRaw).trim() !== "" &&
      flatPayload[MATERIALS_JSON_FIELD_ID] === undefined
    ) {
      errors.push(
        `Row ${i + 2}: could not parse material totals JSON in column "${materialsCol}" (check empty "" fields and keep JSON on one line or fully quoted in CSV)`
      );
    }
    const match = findExpansionMatch(indexes, flatPayload, matchField);
    const body = flatPayloadToExpansionBody(flatPayload, match?.acf_groups_json);

    try {
      let row = null;
      if (match?.id != null) {
        row = await patchProject(base, match.id, body, headers, indexes);
        registerProjectInIndexes(indexes, row);
        updated += 1;
      } else if (createIfMissing && hasExpansionIdentity(flatPayload, matchField)) {
        row = await createThenPatch(base, body, headers, indexes);
        registerProjectInIndexes(indexes, row);
        created += 1;
      } else {
        skipped += 1;
      }
    } catch (e) {
      const apiErr = e?.response?.data?.error;
      errors.push(
        typeof apiErr === "string" ? apiErr : e?.message || `Row ${i + 2}: import failed`
      );
    }

    if (typeof onProgress === "function") {
      onProgress({
        current: i + 1,
        total: csvRows.length,
        created,
        updated,
        skipped,
        errors,
      });
    }
  }

  return { total: csvRows.length, created, updated, skipped, errors };
}

export { EXPANSION_GROUP_LIST };
